import { CARRIER_LABEL, carrierDe, LastQuote, RateResult } from "../types";
import Button from "../../../components/ui/Button";

type ServiceSelectionProps = {
  quote: LastQuote;
  onPick: (rate: RateResult) => void;
};

function deliveredTypeLabel(type: string) {
  if (type === "D") return "Domicilio";
  if (type === "S") return "Sucursal";
  return type || "-";
}

function formatPrice(price: number | string) {
  if (typeof price === "number") {
    return `$ ${price.toLocaleString("es-AR")}`;
  }

  return price;
}

function minutosDesde(fecha: string) {
  const ms = Date.now() - new Date(fecha).getTime();

  return Number.isFinite(ms) ? Math.max(0, Math.floor(ms / 60_000)) : null;
}

// Los resultados de la última cotización, elegibles: cada tarifa puede
// convertirse en una orden. El precio y el bulto los va a congelar el
// servidor desde la cotización guardada — acá solo se elige cuál.
export function ServiceSelection({ quote, onPick }: ServiceSelectionProps) {
  const edad = minutosDesde(quote.createdAt);

  if (quote.rates.length === 0) {
    return (
      <div
        style={{
          border: "1px dashed var(--border)",
          borderRadius: 18,
          padding: 28,
          color: "var(--text-secondary)",
          textAlign: "center",
          background: "var(--surface-alt)",
        }}
      >
        Ningún transportista devolvió tarifas para ese bulto.
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <p style={{ margin: 0, color: "var(--text-secondary)", fontSize: 13 }}>
        Cotización #{quote.id} · {quote.postalCodeOrigin} → {quote.postalCodeDestination}
        {edad !== null && (
          <>
            {" · "}
            <span style={{ color: edad >= 30 ? "var(--danger)" : "inherit" }}>
              {edad >= 30
                ? "vencida: cotizá de nuevo para poder cobrar"
                : `hecha hace ${edad} min (vale 30 min)`}
            </span>
          </>
        )}
      </p>

      {/* Un transportista que falló no tapa al otro: se avisa y se sigue. */}
      {Object.entries(quote.carrierErrors ?? {}).map(([carrier, mensaje]) => (
        <p key={carrier} style={{ margin: 0, color: "var(--warning, #b7791f)", fontSize: 13 }}>
          {CARRIER_LABEL[carrier as keyof typeof CARRIER_LABEL] ?? carrier} no cotizó: {mensaje}
        </p>
      ))}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
          gap: 14,
        }}
      >
        {quote.rates.map((rate, index) => {
          const elegible = Boolean(rate.productType) && rate.deliveredType !== "ALL";

          return (
            <div
              key={`${carrierDe(rate)}-${rate.service}-${rate.deliveredType}-${index}`}
              style={{
                border: "1px solid var(--border)",
                borderRadius: 20,
                padding: 18,
                background: "var(--surface-alt)",
                boxShadow: "var(--shadow)",
                display: "grid",
                gap: 10,
              }}
            >
              <div
                style={{
                  color: "var(--primary)",
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                }}
              >
                {CARRIER_LABEL[carrierDe(rate)]} · {deliveredTypeLabel(rate.deliveredType)}
                {carrierDe(rate) === "correo" && rate.productType ? ` · ${rate.productType}` : ""}
              </div>

              <div style={{ color: "var(--text-primary)", fontWeight: 700 }}>
                {rate.service}
              </div>

              <div style={{ color: "var(--text-primary)", fontSize: 22, fontWeight: 800 }}>
                {formatPrice(rate.price)}
              </div>

              <div style={{ color: "var(--text-secondary)", fontSize: 13 }}>
                Plazo: {rate.sla}
              </div>

              <Button
                variant="primary"
                fullWidth
                onClick={() => onPick(rate)}
                disabled={!elegible}
              >
                {elegible ? "Generar orden" : "Sin tipo de producto"}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
